<template>
  <div class="cpa" :style="cssProps">
    <div class="cpa__phone">
      <!-- Nav-like header, mirroring the mobile screen -->
      <header class="cpa__header">
        <h1 class="cpa__header-title">Positive Actions</h1>
      </header>

      <!-- Expired / invalid link: nothing else on the page is meaningful without a token -->
      <div v-if="error === 'expired'" class="cpa__center">
        <v-icon size="48" color="grey">bhi-close-circle</v-icon>
        <h2 class="cpa__msg-title">This link is no longer valid</h2>
        <p class="cpa__msg">Ask your advisor for a fresh link to your positive actions.</p>
      </div>

      <div v-else-if="error === 'generic' && !loaded" class="cpa__center">
        <v-icon size="48" color="grey">bhi-close-circle</v-icon>
        <h2 class="cpa__msg-title">Something went wrong</h2>
        <p class="cpa__msg">Please try opening the link again.</p>
      </div>

      <template v-else>
        <!-- Month + horizontal day strip -->
        <div class="cpa__calendar">
          <div class="cpa__month">{{ monthLabel }}</div>
          <div ref="strip" class="cpa__strip">
            <button
              type="button"
              class="cpa__day cpa__day--arrow"
              aria-label="Previous month"
              @click="shiftMonth('prev')"
            >
              <v-icon size="18">bhi-chevron-left</v-icon>
            </button>

            <div v-for="day in days" :key="day.date" class="cpa__day-slot">
              <button
                :ref="'day-' + day.date"
                type="button"
                class="cpa__day"
                :class="{
                  'cpa__day--future': day.isAfterToday,
                  'cpa__day--selected': day.date === selectedDate,
                  'cpa__day--today': day.isToday && day.date !== selectedDate,
                }"
                @click="selectDate(day.date)"
              >
                <span class="cpa__day-weekday">{{ day.weekDay }}</span>
                <span class="cpa__day-number">{{ day.day }}</span>
              </button>
              <span v-if="day.hasItems" class="cpa__day-dot" />
            </div>

            <button
              type="button"
              class="cpa__day cpa__day--arrow"
              aria-label="Next month"
              @click="shiftMonth('next')"
            >
              <v-icon size="18">bhi-chevron-right</v-icon>
            </button>
          </div>
        </div>

        <!-- Day body -->
        <main class="cpa__body">
          <div v-if="loading" class="cpa__center">
            <v-progress-circular indeterminate size="36" color="primary" />
          </div>

          <template v-else>
            <!-- AM -->
            <div class="cpa__separator">
              <v-icon size="16" class="cpa__separator-icon">bhi-clock</v-icon>
              <span class="cpa__separator-text">AM Actions</span>
            </div>
            <ActionRow
              v-for="item in morningActions"
              :key="'am-' + item.activity.id"
              :item="item"
              :busy="busyActivityId === item.activity.id"
              @open="openDetail(item)"
              @toggle="onToggleDone(item, $event)"
            />

            <!-- PM -->
            <div class="cpa__separator">
              <v-icon size="16" class="cpa__separator-icon">bhi-clock</v-icon>
              <span class="cpa__separator-text">PM Actions</span>
            </div>
            <ActionRow
              v-for="item in eveningActions"
              :key="'pm-' + item.activity.id"
              :item="item"
              :busy="busyActivityId === item.activity.id"
              @open="openDetail(item)"
              @toggle="onToggleDone(item, $event)"
            />

            <p v-if="!dayActions.length" class="cpa__empty">No Actions today</p>

            <!-- Day rating, as on the mobile screen -->
            <button
              v-if="!isFuture && !dayRating"
              type="button"
              class="cpa__day-rate"
              @click="openDayRating"
            >
              <v-icon size="20" class="cpa__day-rate-icon">bhi-star-empty</v-icon>
              How did you like today's Actions?
            </button>
            <div v-else-if="!isFuture" class="cpa__day-rated">
              <StarRating :value="dayRating" :readonly="true" />
              <button type="button" class="cpa__link-btn" @click="openDayRating">Change</button>
            </div>
          </template>
        </main>
      </template>
    </div>

    <!-- Action detail / answer sheet -->
    <div v-if="detail" class="cpa__sheet-backdrop" @click.self="closeDetail">
      <section class="cpa__sheet">
        <header class="cpa__sheet-header">
          <h2 class="cpa__sheet-title">{{ detail.activity.title }}</h2>
          <button type="button" class="cpa__sheet-close" aria-label="Close" @click="closeDetail">
            <v-icon size="20">bhi-close</v-icon>
          </button>
        </header>

        <img
          v-if="mediaUrl(detail)"
          :src="mediaUrl(detail)"
          :alt="detail.activity.title"
          class="cpa__sheet-media"
        />

        <p v-if="detail.activity.description" class="cpa__sheet-desc">
          {{ detail.activity.description }}
        </p>
        <a
          v-if="detail.activity.externalUrl"
          :href="detail.activity.externalUrl"
          target="_blank"
          rel="noopener"
          class="cpa__link-btn"
        >
          Open link
        </a>

        <div class="cpa__sheet-row">
          <span class="cpa__sheet-label">Did you do it?</span>
          <Toggle
            :value="detail.activity.completed"
            :disabled="busyActivityId === detail.activity.id"
            @input="onToggleDone(detail, $event)"
          />
        </div>

        <div class="cpa__sheet-block">
          <span class="cpa__sheet-label">Rate this action</span>
          <StarRating
            :value="detail.activity.rating || 0"
            @input="onRateActivity(detail, $event)"
          />
          <textarea
            v-model="detailComment"
            class="cpa__textarea"
            rows="2"
            placeholder="Add a comment (optional)"
          />
          <div class="cpa__sheet-actions">
            <button
              v-if="detail.activity.rating"
              type="button"
              class="cpa__link-btn"
              @click="onDeleteActivityRating(detail)"
            >
              Remove rating
            </button>
            <button
              type="button"
              class="cpa__btn"
              :disabled="!detail.activity.rating"
              @click="onRateActivity(detail, detail.activity.rating)"
            >
              Save
            </button>
          </div>
        </div>
      </section>
    </div>

    <!-- Day rating sheet -->
    <div v-if="dayRatingOpen" class="cpa__sheet-backdrop" @click.self="dayRatingOpen = false">
      <section class="cpa__sheet">
        <header class="cpa__sheet-header">
          <h2 class="cpa__sheet-title">How do you feel about today's Actions?</h2>
          <button
            type="button"
            class="cpa__sheet-close"
            aria-label="Close"
            @click="dayRatingOpen = false"
          >
            <v-icon size="20">bhi-close</v-icon>
          </button>
        </header>
        <StarRating :value="dayRatingDraft" @input="dayRatingDraft = $event" />
        <textarea
          v-model="dayCommentDraft"
          class="cpa__textarea"
          rows="2"
          placeholder="Add a comment (optional)"
        />
        <div class="cpa__sheet-actions">
          <button v-if="dayRating" type="button" class="cpa__link-btn" @click="onDeleteDayRating">
            Remove rating
          </button>
          <button type="button" class="cpa__btn" :disabled="!dayRatingDraft" @click="onRateDay">
            Save
          </button>
        </div>
      </section>
    </div>

    <v-snackbar v-model="snackbar" :timeout="4000" bottom>{{ snackbarText }}</v-snackbar>
  </div>
</template>

<script>
import { apiUnloggedInstance } from '@/api/api.js'
import Config from '@/config.json'
import extendedConfig from '@/tenants.js'
import ActionRow from '@/components/positiveActions/ActionRow.vue'
import StarRating from '@/components/positiveActions/StarRating.vue'
import Toggle from '@/components/positiveActions/Toggle.vue'

const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** 'YYYY-MM-DD' for a local Date, avoiding the UTC shift toISOString() would introduce. */
const toIso = date => {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return date.getFullYear() + '-' + month + '-' + day
}

const parseIso = iso => {
  const parts = iso.split('-').map(Number)
  return new Date(parts[0], parts[1] - 1, parts[2])
}

/**
 * The backend DTOs are serialized NON_NULL, so an unanswered action arrives with `rating` and
 * `ratingComment` absent entirely. Vue 2 cannot make a property reactive after the fact, so seed
 * the answer fields here — without this, saving a rating would not re-render the stars.
 */
const normalizeItem = item => {
  if (!item.activity) return item
  const activity = item.activity
  return {
    ...item,
    activity: {
      ...activity,
      completed: !!activity.completed,
      rating: activity.rating || 0,
      ratingComment: activity.ratingComment || '',
    },
  }
}

export default {
  name: 'ClientPositiveActions',
  components: { ActionRow, StarRating, Toggle },
  data() {
    const today = toIso(new Date())
    return {
      token: null,
      today,
      selectedDate: today,
      // Keyed by 'YYYY-MM-DD' -> { rating, ratingComment, items } for the loaded month.
      calendar: {},
      loading: true,
      loaded: false,
      error: null, // 'expired' | 'generic' | null
      busyActivityId: null,
      detail: null,
      detailComment: '',
      dayRatingOpen: false,
      dayRatingDraft: 0,
      dayCommentDraft: '',
      snackbar: false,
      snackbarText: '',
    }
  },
  computed: {
    // /prompt/** and /pa/** live at the backend ROOT, but VUE_APP_BACKEND_URL points at .../api.
    // Strip the /api suffix and call an absolute URL so axios ignores the /api baseURL.
    backendRoot() {
      return (Config.VUE_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')
    },
    cssProps() {
      return {
        '--accent': extendedConfig.VUE_CSS_BH_BLUE || '#00a0e0',
        '--primary': extendedConfig.VUE_CSS_MAIN_COLOR || '#213665',
      }
    },
    monthLabel() {
      const date = parseIso(this.selectedDate)
      return MONTH_NAMES[date.getMonth()] + ' ' + date.getFullYear()
    },
    /** Every day of the selected month, with the flags the strip renders from. */
    days() {
      const selected = parseIso(this.selectedDate)
      const year = selected.getFullYear()
      const month = selected.getMonth()
      const total = new Date(year, month + 1, 0).getDate()
      const out = []
      for (let day = 1; day <= total; day += 1) {
        const date = new Date(year, month, day)
        const iso = toIso(date)
        out.push({
          date: iso,
          day,
          weekDay: WEEKDAY_INITIALS[date.getDay()],
          isToday: iso === this.today,
          isAfterToday: iso > this.today,
          hasItems: !!(this.calendar[iso] && this.calendar[iso].items.length),
        })
      }
      return out
    },
    /** ACTIVITY-type items only: exercises and series are a separate mobile screen. */
    dayActions() {
      const day = this.calendar[this.selectedDate]
      if (!day) return []
      return day.items.filter(item => item.type === 'ACTIVITY' && item.activity)
    },
    // An activity with no time set counts as morning, matching the mobile grouping.
    morningActions() {
      return this.dayActions.filter(
        item => item.activity.time === 'MORNING' || !item.activity.time,
      )
    },
    eveningActions() {
      return this.dayActions.filter(item => item.activity.time === 'EVENING')
    },
    dayRating() {
      const day = this.calendar[this.selectedDate]
      return (day && day.rating) || 0
    },
    /**
     * Only gates the whole-day rating, matching mobile: individual actions stay answerable on any
     * date, but there is nothing to rate about a day that has not happened.
     */
    isFuture() {
      return this.selectedDate > this.today
    },
  },
  created() {
    this.token = this.$route.query.t || null
    if (!this.token) {
      this.loading = false
      this.error = 'expired'
      return
    }
    this.loadMonth()
  },
  methods: {
    monthBounds(iso) {
      const date = parseIso(iso)
      const first = new Date(date.getFullYear(), date.getMonth(), 1)
      const last = new Date(date.getFullYear(), date.getMonth() + 1, 0)
      return { startDate: toIso(first), endDate: toIso(last) }
    },
    async loadMonth() {
      this.loading = true
      const bounds = this.monthBounds(this.selectedDate)
      try {
        const res = await apiUnloggedInstance().get(
          this.backendRoot + '/prompt/positive-actions/calendar',
          { params: { t: this.token, startDate: bounds.startDate, endDate: bounds.endDate } },
        )
        const items = (res.data && res.data.items) || []
        const next = {}
        items.forEach(day => {
          next[day.date] = {
            rating: day.rating || 0,
            ratingComment: day.ratingComment || '',
            items: (day.items || []).map(normalizeItem),
          }
        })
        this.calendar = next
        this.loaded = true
        this.error = null
        this.$nextTick(this.scrollToSelected)
      } catch (e) {
        this.handleError(e, 'Could not load your positive actions.')
      } finally {
        this.loading = false
      }
    },
    selectDate(iso) {
      const sameMonth = iso.slice(0, 7) === this.selectedDate.slice(0, 7)
      this.selectedDate = iso
      if (sameMonth) {
        this.$nextTick(this.scrollToSelected)
      } else {
        this.loadMonth()
      }
    },
    /** Land on today when stepping into the current month, as the mobile screen does. */
    shiftMonth(direction) {
      const date = parseIso(this.selectedDate)
      const target =
        direction === 'next'
          ? new Date(date.getFullYear(), date.getMonth() + 1, 1)
          : new Date(date.getFullYear(), date.getMonth(), 0)
      const iso = toIso(target)
      this.selectedDate = iso.slice(0, 7) === this.today.slice(0, 7) ? this.today : iso
      this.loadMonth()
    },
    scrollToSelected() {
      const strip = this.$refs.strip
      const ref = this.$refs['day-' + this.selectedDate]
      const el = Array.isArray(ref) ? ref[0] : ref
      if (!strip || !el) return
      strip.scrollLeft = el.offsetLeft - strip.clientWidth / 2 + el.clientWidth / 2
    },
    mediaUrl(item) {
      const activity = item.activity
      const additional = activity.additionalMedia || {}
      const media = activity.media || {}
      const staticMedia = activity.mobileStaticMedia || {}
      return (
        additional.previewUrl ||
        additional.thumbnailUrl ||
        media.previewUrl ||
        media.thumbnailUrl ||
        staticMedia.mediaUrl ||
        null
      )
    },
    openDetail(item) {
      this.detail = item
      this.detailComment = item.activity.ratingComment || ''
    },
    closeDetail() {
      this.detail = null
      this.detailComment = ''
    },
    async onToggleDone(item, completed) {
      const activity = item.activity
      const previous = activity.completed
      // Optimistic, as mobile is - the row reverts if the write fails.
      activity.completed = completed
      this.busyActivityId = activity.id
      try {
        await apiUnloggedInstance().put(
          this.backendRoot + '/prompt/positive-actions/activities/' + activity.id + '/complete',
          { completed },
          { params: { t: this.token } },
        )
      } catch (e) {
        activity.completed = previous
        this.handleError(e, 'Could not save that. Please try again.')
      } finally {
        this.busyActivityId = null
      }
    },
    async onRateActivity(item, rating) {
      if (!rating) return
      const activity = item.activity
      const previousRating = activity.rating
      const previousComment = activity.ratingComment
      activity.rating = rating
      activity.ratingComment = this.detailComment
      this.busyActivityId = activity.id
      try {
        await apiUnloggedInstance().put(
          this.backendRoot + '/prompt/positive-actions/activities/' + activity.id + '/rating',
          { rating, ratingComment: this.detailComment || null },
          { params: { t: this.token } },
        )
        this.notify('Rating saved.')
      } catch (e) {
        activity.rating = previousRating
        activity.ratingComment = previousComment
        this.handleError(e, 'Could not save your rating.')
      } finally {
        this.busyActivityId = null
      }
    },
    async onDeleteActivityRating(item) {
      const activity = item.activity
      const previousRating = activity.rating
      const previousComment = activity.ratingComment
      activity.rating = null
      activity.ratingComment = null
      this.detailComment = ''
      try {
        await apiUnloggedInstance().delete(
          this.backendRoot + '/prompt/positive-actions/activities/' + activity.id + '/rating',
          { params: { t: this.token } },
        )
      } catch (e) {
        activity.rating = previousRating
        activity.ratingComment = previousComment
        this.handleError(e, 'Could not remove your rating.')
      }
    },
    openDayRating() {
      const day = this.calendar[this.selectedDate]
      this.dayRatingDraft = (day && day.rating) || 0
      this.dayCommentDraft = (day && day.ratingComment) || ''
      this.dayRatingOpen = true
    },
    async onRateDay() {
      try {
        await apiUnloggedInstance().put(
          this.backendRoot + '/prompt/positive-actions/days/' + this.selectedDate + '/rating',
          { rating: this.dayRatingDraft, ratingComment: this.dayCommentDraft || null },
          { params: { t: this.token } },
        )
        this.setDay({ rating: this.dayRatingDraft, ratingComment: this.dayCommentDraft })
        this.dayRatingOpen = false
      } catch (e) {
        this.handleError(e, 'Could not save your rating.')
      }
    },
    async onDeleteDayRating() {
      try {
        await apiUnloggedInstance().delete(
          this.backendRoot + '/prompt/positive-actions/days/' + this.selectedDate + '/rating',
          { params: { t: this.token } },
        )
        this.setDay({ rating: 0, ratingComment: '' })
        this.dayRatingOpen = false
      } catch (e) {
        this.handleError(e, 'Could not remove your rating.')
      }
    },
    /** Merge a day-level change into the reactive calendar map. */
    setDay(patch) {
      const existing = this.calendar[this.selectedDate] || { items: [] }
      this.$set(this.calendar, this.selectedDate, Object.assign({}, existing, patch))
    },
    handleError(e, fallback) {
      const status = e && e.response && e.response.status
      if (status === 401 || status === 410) {
        this.error = 'expired'
        return
      }
      this.error = 'generic'
      this.notify(fallback)
    },
    notify(text) {
      this.snackbarText = text
      this.snackbar = true
    },
  },
}
</script>

<style lang="scss" scoped>
// Mirrors the mobile Positive Actions screen: a single phone-width column, the same palette
// (mobile Variables.js) and the same pill calendar strip / AM-PM separators.
$gray-new: #f6f6fa;
$gray-2: #a0a4a8;
$gray-1: #52575c;
$gray-5: #e8e8e8;
$black: #25282b;

.cpa {
  min-height: 100vh;
  background: #fff;
  display: flex;
  justify-content: center;

  &__phone {
    width: 100%;
    max-width: 480px;
    padding-bottom: 48px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
  }
  &__header-title {
    font-size: 18px;
    font-weight: 700;
    line-height: 28px;
    color: $gray-2;
    margin: 0;
  }

  &__calendar {
    padding-bottom: 8px;
  }
  &__month {
    font-size: 14px;
    font-weight: 700;
    color: #000;
    padding: 8px 32px;
  }
  &__strip {
    display: flex;
    align-items: flex-start;
    overflow-x: auto;
    padding: 0 12px 4px;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  &__day-slot {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 0 0 auto;
  }
  &__day {
    height: 72px;
    width: 40px;
    border-radius: 24px;
    margin: 4px;
    background: $gray-new;
    border: 1px solid $gray-new;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex: 0 0 auto;
    &--arrow {
      background: transparent;
      border-color: transparent;
      color: $gray-1;
    }
    &--future {
      background: #fff;
      border-color: $gray-new;
    }
    &--today {
      border-color: var(--accent);
    }
    &--selected {
      background: var(--accent);
      border-color: var(--accent);
      .cpa__day-weekday,
      .cpa__day-number {
        color: #fff;
      }
    }
  }
  &__day-weekday {
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    color: var(--accent);
  }
  &__day-number {
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    color: $gray-1;
  }
  &__day-dot {
    width: 5px;
    height: 5px;
    border-radius: 3px;
    background: var(--accent);
  }

  &__body {
    padding: 16px 24px 0;
  }
  &__separator {
    display: flex;
    align-items: center;
    padding: 8px 16px;
    border-radius: 99px;
    border: 2px solid $gray-new;
    background: #fafafa;
    margin-bottom: 12px;
  }
  &__separator-icon {
    color: $gray-2 !important;
    margin-right: 12px;
  }
  &__separator-text {
    font-size: 14px;
    font-weight: 700;
    line-height: 24px;
    color: #000;
  }
  &__empty {
    text-align: center;
    color: $gray-2;
    font-size: 14px;
    margin: 24px 0;
  }

  &__day-rate {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 24px auto 0;
    padding: 12px 16px;
    border-radius: 99px;
    background: $gray-new;
    border: none;
    color: var(--accent);
    font-weight: 700;
    font-size: 14px;
    cursor: pointer;
  }
  &__day-rate-icon {
    color: var(--accent) !important;
    margin-right: 8px;
  }
  &__day-rated {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 24px;
    > * + * {
      margin-left: 12px;
    }
  }

  &__center {
    text-align: center;
    padding: 64px 24px;
    color: $gray-1;
  }
  &__msg-title {
    font-size: 18px;
    margin: 16px 0 4px;
    color: $black;
  }
  &__msg {
    margin: 0;
    font-size: 14px;
  }

  &__sheet-backdrop {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 20;
  }
  &__sheet {
    background: #fff;
    width: 100%;
    max-width: 480px;
    border-radius: 16px 16px 0 0;
    padding: 20px;
    max-height: 88vh;
    overflow-y: auto;
  }
  &__sheet-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }
  &__sheet-title {
    font-size: 18px;
    font-weight: 700;
    color: $black;
    margin: 0;
  }
  &__sheet-close {
    background: none;
    border: none;
    cursor: pointer;
    color: $gray-1;
    margin-left: 12px;
  }
  &__sheet-media {
    width: 100%;
    max-height: 200px;
    object-fit: cover;
    border-radius: 12px;
    margin-top: 12px;
  }
  &__sheet-desc {
    font-size: 14px;
    color: $gray-1;
    margin: 12px 0 0;
    white-space: pre-line;
  }
  &__sheet-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid $gray-new;
  }
  &__sheet-block {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid $gray-new;
    display: flex;
    flex-direction: column;
    > * + * {
      margin-top: 8px;
    }
  }
  &__sheet-label {
    font-size: 14px;
    font-weight: 700;
    color: $black;
  }
  &__sheet-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    > * + * {
      margin-left: 16px;
    }
  }
  &__textarea {
    width: 100%;
    border: 1px solid $gray-5;
    border-radius: 8px;
    padding: 8px;
    font-size: 14px;
    color: $black;
    resize: vertical;
  }
  &__btn {
    background: var(--accent);
    color: #fff;
    border: none;
    border-radius: 99px;
    padding: 8px 24px;
    font-weight: 700;
    font-size: 14px;
    cursor: pointer;
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
  &__link-btn {
    background: none;
    border: none;
    padding: 0;
    color: var(--accent);
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    text-decoration: none;
  }
}
</style>
